import { useEffect, useRef, useCallback } from 'react'

interface AudioBarsVisualizerProps {
  isActive: boolean
  isSpeaking: boolean
  className?: string
  theme?: 'light' | 'dark'
  accentColor?: string
  getInputByteFrequencyData?: () => Uint8Array | undefined
  getOutputByteFrequencyData?: () => Uint8Array | undefined
}

const BAR_WIDTH = 3
const BAR_GAP = 3
const BAR_RADIUS = 1.5
const SMOOTHING = 0.55

export function AudioBarsVisualizer({
  isActive,
  isSpeaking,
  className = '',
  getInputByteFrequencyData,
  getOutputByteFrequencyData,
}: AudioBarsVisualizerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number | undefined>(undefined)
  const barsDataRef = useRef<number[]>([])
  const targetBarsRef = useRef<number[]>([])
  const sizeRef = useRef({ width: 0, height: 0, barCount: 0 })

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const rect = container.getBoundingClientRect()

    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${rect.height}px`

    const ctx = canvas.getContext('2d')
    if (ctx) ctx.scale(dpr, dpr)

    const barCount = Math.floor(rect.width / (BAR_WIDTH + BAR_GAP))
    sizeRef.current = { width: rect.width, height: rect.height, barCount }

    if (barsDataRef.current.length !== barCount) {
      barsDataRef.current = new Array(barCount).fill(0)
      targetBarsRef.current = new Array(barCount).fill(0)
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    setupCanvas()
    const resizeObserver = new ResizeObserver(() => setupCanvas())
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [setupCanvas])

  useEffect(() => {
    if (!isActive) return

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const draw = () => {
      if (!isActive) return
      animationRef.current = requestAnimationFrame(draw)

      const { width, height, barCount } = sizeRef.current
      if (barCount === 0) return

      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.restore()

      const freqData = isSpeaking
        ? getOutputByteFrequencyData?.()
        : getInputByteFrequencyData?.()

      if (freqData && freqData.length > 0) {
        const usableBins = Math.floor(freqData.length * 0.6)
        const half = Math.ceil(barCount / 2)
        for (let i = 0; i < half; i++) {
          const dataIndex = Math.floor((i / half) * usableBins)
          const val = Math.max(0.03, freqData[dataIndex] / 255)
          targetBarsRef.current[half - 1 - i] = val
          if (half + i < barCount) targetBarsRef.current[half + i] = val
        }
      } else {
        for (let i = 0; i < barCount; i++) {
          targetBarsRef.current[i] *= 0.88
        }
      }

      for (let i = 0; i < barCount; i++) {
        barsDataRef.current[i] += (targetBarsRef.current[i] - barsDataRef.current[i]) * SMOOTHING
      }

      const centerY = height / 2
      const totalWidth = barCount * (BAR_WIDTH + BAR_GAP) - BAR_GAP
      const offsetX = (width - totalWidth) / 2

      for (let i = 0; i < barCount; i++) {
        const x = offsetX + i * (BAR_WIDTH + BAR_GAP)
        const val = barsDataRef.current[i]
        const barH = Math.max(2, val * height * 0.85)
        const y = centerY - barH / 2

        ctx.fillStyle = `rgba(255, 255, 255, ${0.45 + val * 0.55})`
        ctx.beginPath()
        ctx.roundRect(x, y, BAR_WIDTH, barH, BAR_RADIUS)
        ctx.fill()
      }
    }

    animationRef.current = requestAnimationFrame(draw)

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [isActive, isSpeaking, getInputByteFrequencyData, getOutputByteFrequencyData])

  if (!isActive) return null

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  )
}